// ============================================================
// Risk Control System - Web SDK
// 设备指纹采集 + 行为数据采集 + 自动上报
// （不再直接调用风控评估 API，决策由商户服务端完成）
// ============================================================

import { SDK_VERSION } from './version'

export interface SDKConfig {
  /** 商户 ID（必填），用于运营维度的数据隔离、限流、监控 */
  merchantId: string
  /** 风控服务端点 */
  endpoint: string
  /** __rc_token Cookie 域名（默认当前域名） */
  cookieDomain?: string
  /** __rc_token Cookie 路径（默认 /） */
  cookiePath?: string
  /** __rc_token 过期秒数（默认 2592000 = 30天） */
  cookieMaxAge?: number
  /** 是否启用设备指纹采集（默认 true） */
  enableDeviceFingerprint?: boolean
  /** 是否启用行为采集（默认 true） */
  enableBehaviorTracking?: boolean
  /** 调试模式 */
  debug?: boolean
  /** 请求超时毫秒（默认 3000） */
  timeout?: number
}

export interface DeviceFingerprint {
  device_fingerprint: string
  canvas_hash: string
  webgl_hash: string
  webgl_vendor: string
  webgl_renderer: string
  audio_hash: string
  fonts_hash: string
  plugins_hash: string
  screen_resolution: string
  color_depth: number
  timezone: string
  timezone_offset: number
  language: string
  platform: string
  hardware_concurrency: number
  device_memory: number
  touch_support: boolean
  cookie_enabled: boolean
  browser: string
  browser_version: string
  os: string
  user_agent: string
}

export interface BehaviorData {
  form_fill_duration_ms: number    // 表单填写耗时（仅焦点在表单项时累计）
  page_session_duration_ms: number // 页面总停留时长（SDK 初始化到当前）
  mouse_movements: boolean
  keystrokes: number
  paste_count: number
  page_visible: boolean
  touch_supported: boolean
  page_history?: PageVisit[]       // 近 10 分钟页面访问历史
}

export interface PageVisit {
  path: string       // 页面路径，如 "/settings/password"
  duration_ms: number // 在该页面的停留时长（毫秒）
  referrer?: string  // 页面来源 referrer（外部引流来源分析）
}

// ============================================================
// Cookie + localStorage 工具
// ============================================================

const LS_TOKEN_KEY = '__rc_token'
const COOKIE_TOKEN_KEY = '__rc_token'

/** 从 Cookie 中读取指定名称的值 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${encodeURIComponent(name)}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie(name: string, value: string, options: {
  domain?: string
  path?: string
  maxAge?: number
  sameSite?: 'Lax' | 'Strict' | 'None'
  secure?: boolean
}): void {
  const parts: string[] = [`${encodeURIComponent(name)}=${encodeURIComponent(value)}`]
  if (options.path) parts.push(`path=${options.path}`)
  if (options.domain) parts.push(`domain=${options.domain}`)
  if (options.maxAge != null) parts.push(`max-age=${options.maxAge}`)
  if (options.sameSite) parts.push(`SameSite=${options.sameSite}`)
  if (options.secure) parts.push('Secure')
  document.cookie = parts.join('; ')
}

/** 读取 localStorage 中的 rc_token（跨 session 持久化） */
function loadRcToken(): string | null {
  try {
    return localStorage.getItem(LS_TOKEN_KEY)
  } catch {
    return null
  }
}

/** 保存 rc_token 到 localStorage */
function saveRcToken(token: string): void {
  try {
    localStorage.setItem(LS_TOKEN_KEY, token)
  } catch {
    // localStorage 不可用时（如 Safari 无痕模式）静默失败
  }
}

// ============================================================
// 设备指纹采集器
// 策略：Worker 处理 CPU 密集的字体测量；主线程处理 Canvas、WebGL、Audio
// 最终复合指纹使用 SHA-256，大幅降低碰撞概率
// ============================================================

/** 计算 SHA-256 十六进制摘要（降级到 FNV-1a 当 crypto.subtle 不可用） */
async function sha256Hex(input: string): Promise<string> {
  try {
    if (crypto.subtle) {
      const data = new TextEncoder().encode(input)
      const hashBuf = await crypto.subtle.digest('SHA-256', data)
      const hashArr = Array.from(new Uint8Array(hashBuf))
      return hashArr.map(b => b.toString(16).padStart(2, '0')).join('')
    }
  } catch {
    // crypto.subtle 不可用（如 HTTP 环境），降级
  }
  return fnv1a(input)
}

/** FNV-1a 32-bit 哈希（用于组件级哈希，速度极快） */
function fnv1a(str: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16)
}

class FingerprintCollector {
  private static worker: Worker | null = null
  private static workerBlobUrl: string | null = null

  /** 创建字体指纹采集 Worker（字体测量 CPU 密集，放入 Worker 不阻塞主线程） */
  private static getFontWorker(): Worker {
    if (this.worker) return this.worker

    const code = `
      const fnv1a = (str) => {
        let hash = 0x811c9dc5
        for (let i = 0; i < str.length; i++) {
          hash ^= str.charCodeAt(i)
          hash = Math.imul(hash, 0x01000193)
        }
        return (hash >>> 0).toString(16)
      }

      const fontsFingerprint = () => {
        try {
          const c = new OffscreenCanvas(256, 256)
          const ctx = c.getContext('2d')
          if (!ctx) return ''
          const fonts = ['Arial','Verdana','Times New Roman','Courier New','Helvetica',
            'Comic Sans MS','Georgia','Impact','Trebuchet MS','monospace']
          const base = ['monospace','sans-serif','serif']
          const r = []
          for (const f of fonts) for (const b of base) {
            ctx.font = "72px '" + f + "', " + b
            r.push(f + '-' + b + ':' + ctx.measureText('mmmmmmmmmmlli').width)
          }
          return fnv1a(r.join('|'))
        } catch { return '' }
      }

      self.onmessage = () => { self.postMessage({ fonts_hash: fontsFingerprint() }) }
    `

    const blob = new Blob([code], { type: 'application/javascript' })
    this.workerBlobUrl = URL.createObjectURL(blob)
    this.worker = new Worker(this.workerBlobUrl)
    return this.worker
  }

  static async collect(): Promise<DeviceFingerprint> {
    const screenRes = `${screen.width}x${screen.height}`
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const tzOffset = new Date().getTimezoneOffset()
    const lang = navigator.language
    const platform = navigator.platform
    const hwConcurrency = navigator.hardwareConcurrency || 0
    const devMemory = (navigator as any).deviceMemory || 0
    const touchSupport = 'ontouchstart' in window
    const cookieEnabled = navigator.cookieEnabled
    const ua = navigator.userAgent

    // 从 UA 解析浏览器名称/版本
    let browser = '', browserVersion = ''
    if (ua.includes('Edg/')) {
      browser = 'Edge'; browserVersion = (ua.match(/Edg\/(\d+)/) || [])[1] || ''
    } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
      browser = 'Chrome'; browserVersion = (ua.match(/Chrome\/(\d+)/) || [])[1] || ''
    } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
      browser = 'Safari'; browserVersion = (ua.match(/Version\/(\d+)/) || [])[1] || ''
    } else if (ua.includes('Firefox/')) {
      browser = 'Firefox'; browserVersion = (ua.match(/Firefox\/(\d+)/) || [])[1] || ''
    }

    // 从 UA 解析操作系统
    let os = ''
    if (ua.includes('Windows NT')) {
      os = 'Windows'
    } else if (ua.includes('Mac OS X') || ua.includes('macOS')) {
      os = 'macOS'
    } else if (ua.includes('Android')) {
      os = 'Android'
    } else if (ua.includes('iPhone') || ua.includes('iPad')) {
      os = 'iOS'
    } else if (ua.includes('Linux')) {
      os = 'Linux'
    }

    // 并行收集各指纹组件
    const [canvasHash, webglInfo, audioHash, fontsHash] = await Promise.all([
      this.collectCanvasFingerprint(),
      this.collectWebGLFingerprint(),
      this.collectAudioFingerprint(),
      this.collectFontsFingerprint(),
    ])

    // Plugins 指纹
    const pluginsHash = this.collectPluginsFingerprint()

    // 生成复合 device_fingerprint（SHA-256，256bit）
    const fpComponents = [canvasHash, webglInfo.hash, audioHash, fontsHash, pluginsHash]
      .filter(Boolean)
      .join('|')
    const deviceFP = fpComponents
      ? `fp_${await sha256Hex(fpComponents)}`
      : ''

    return {
      device_fingerprint: deviceFP,
      canvas_hash: canvasHash,
      webgl_hash: webglInfo.hash,
      webgl_vendor: webglInfo.vendor,
      webgl_renderer: webglInfo.renderer,
      audio_hash: audioHash,
      fonts_hash: fontsHash,
      plugins_hash: pluginsHash,
      screen_resolution: screenRes,
      color_depth: screen.colorDepth,
      timezone: tz,
      timezone_offset: tzOffset,
      language: lang,
      platform: platform,
      hardware_concurrency: hwConcurrency,
      device_memory: devMemory,
      touch_support: touchSupport,
      cookie_enabled: cookieEnabled,
      browser: browser,
      browser_version: browserVersion,
      os: os,
      user_agent: ua,
    }
  }

  /**
   * Canvas 指纹（主线程）
   * 利用 Canvas 2D API 的排版指标 —— 不读取像素数据，完全规避
   * Safari/Firefox 的反指纹噪声注入，确保同设备同浏览器结果稳定。
   *
   * 测量维度：
   * - 多种字体/字号组合的 textMeasure.width（子像素精度）
   * - 文本行高（actualBoundingBoxAscent + descent）
   * - Canvas 支持的 2D 属性枚举（颜色空间、阴影、合成模式等）
   */
  private static async collectCanvasFingerprint(): Promise<string> {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return ''

      const parts: string[] = []

      // 1. 多字体多字号文字宽度测量（不同 OS/浏览器排版引擎有细微差异）
      const testTexts = [
        'Risk Control System ◕‿◕',
        'abcdefghijklmnopqrstuvwxyz0123456789',
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      ]
      const fontSamples = [
        '12px Arial', '14px Arial', '16px Arial',
        '14px serif', '16px serif',
        '14px sans-serif', '16px sans-serif',
        '14px monospace', '16px monospace',
        '14px "Times New Roman"', '14px "Courier New"',
      ]
      for (const text of testTexts) {
        for (const font of fontSamples) {
          ctx.font = font
          const m = ctx.measureText(text)
          // 保留到小数点后 2 位（子像素精度），忽略极小噪声
          parts.push(String(Math.round(m.width * 100)))
          if (m.actualBoundingBoxAscent) {
            parts.push(String(Math.round(m.actualBoundingBoxAscent * 100)))
          }
          if (m.actualBoundingBoxDescent) {
            parts.push(String(Math.round(m.actualBoundingBoxDescent * 100)))
          }
        }
      }

      // 2. Canvas 2D API 属性枚举（不同浏览器实现有差异）
      parts.push(String(ctx.direction))
      parts.push(String(ctx.filter))
      parts.push(String(ctx.imageSmoothingEnabled))
      parts.push(String(ctx.imageSmoothingQuality))
      parts.push(String(ctx.textAlign))
      parts.push(String(ctx.textBaseline))
      parts.push(String(ctx.globalAlpha))
      parts.push(String(ctx.globalCompositeOperation))

      return fnv1a(parts.join('|'))
    } catch {
      return ''
    }
  }

  /**
   * WebGL 指纹（主线程）
   * GPU 型号、驱动版本、支持的扩展在不同设备间高度唯一
   */
  private static async collectWebGLFingerprint(): Promise<{ hash: string; vendor: string; renderer: string }> {
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl') || (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null)
      if (!gl) return { hash: '', vendor: '', renderer: '' }

      const di = gl.getExtension('WEBGL_debug_renderer_info')
      const vendor = di ? gl.getParameter(di.UNMASKED_VENDOR_WEBGL) : ''
      const renderer = di ? gl.getParameter(di.UNMASKED_RENDERER_WEBGL) : ''
      const params = [
        gl.getParameter(gl.RENDERER),
        gl.getParameter(gl.VENDOR),
        gl.getParameter(gl.MAX_TEXTURE_SIZE),
        gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
        (gl.getParameter(gl.MAX_VIEWPORT_DIMS) || []).join(','),
        (gl.getSupportedExtensions() || []).join(','),
      ].join('|')
      return { hash: fnv1a(params), vendor, renderer }
    } catch {
      return { hash: '', vendor: '', renderer: '' }
    }
  }

  /**
   * Audio 指纹（主线程，使用 OfflineAudioContext）
   * 利用不同设备对音频信号处理的微小差异（DAC 特性、浮点精度）
   * OfflineAudioContext 不需要播放声音，不受自动播放策略限制
   */
  private static async collectAudioFingerprint(): Promise<string> {
    try {
      const ctx = new OfflineAudioContext(1, 44100, 44100)
      const osc = ctx.createOscillator()
      const compressor = ctx.createDynamicsCompressor()
      osc.type = 'sawtooth'
      osc.frequency.value = 440
      compressor.threshold.value = -50
      compressor.knee.value = 40
      compressor.ratio.value = 12
      osc.connect(compressor)
      compressor.connect(ctx.destination)
      osc.start(0)
      const buffer = await ctx.startRendering()
      const samples = buffer.getChannelData(0)
      // 采样前 200 个浮点样本
      const arr: number[] = []
      for (let i = 0; i < 200; i++) {
        arr.push(Math.round(samples[i] * 1e6))
      }
      return fnv1a(arr.join(','))
    } catch {
      return ''
    }
  }

  /**
   * 字体指纹（Web Worker 异步）
   * 测量不同字体渲染同一文字时的宽度，因 OS 安装的字体集合不同而有差异
   */
  private static async collectFontsFingerprint(): Promise<string> {
    try {
      const worker = this.getFontWorker()
      const result = await new Promise<{ fonts_hash: string }>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('timeout')), 200)
        worker.onmessage = (e) => { clearTimeout(timeout); resolve(e.data) }
        worker.onerror = () => { clearTimeout(timeout); reject(new Error('worker error')) }
        worker.postMessage('collect')
      })
      return result.fonts_hash
    } catch {
      return ''
    } finally {
      // 回收 Blob URL，避免内存泄漏
      if (this.workerBlobUrl) {
        URL.revokeObjectURL(this.workerBlobUrl)
        this.workerBlobUrl = null
      }
    }
  }

  /** Plugins 指纹（同步，主线程） */
  private static collectPluginsFingerprint(): string {
    const plugins: string[] = []
    if (navigator.plugins) {
      for (let i = 0; i < navigator.plugins.length; i++) {
        plugins.push(navigator.plugins[i].name)
      }
    }
    return fnv1a(plugins.join(','))
  }
}

// ============================================================
// 行为采集器
// ============================================================

class BehaviorTracker {
  private mouseMoved = false
  private keystrokes = 0
  private pasteCount = 0
  private formFocusTime: number | null = null
  private totalFormFillMs = 0
  private sessionStart: number

  // 页面访问历史追踪（用于操作链检测）
  private pageHistory: PageVisit[] = []
  private currentPage: string = ''
  private currentPageEntry: number = 0
  private readonly PAGE_HISTORY_WINDOW_MS = 10 * 60 * 1000 // 10 分钟

  /** SPA 路由切换回调（由 KsenseClient 注入，用于自动上报行为数据） */
  private onPageVisit: (() => void) | null = null

  constructor(onPageVisit?: () => void) {
    this.onPageVisit = onPageVisit || null
    this.sessionStart = Date.now()
    this.bindEvents()
    // 首次页面访问不上报（构造函数还未返回，调用方尚未完成赋值）
    this.trackPageVisit(this.getCurrentPath(), true /* silent */)
    this.monkeyPatchHistory()
  }

  /** 获取当前页面路径 */
  private getCurrentPath(): string {
    return window.location.pathname + window.location.search
  }

  /** 记录页面访问 */
  private trackPageVisit(path: string, silent = false): void {
    const now = Date.now()
    // 关闭上一页的计时
    if (this.currentPage && this.currentPageEntry > 0) {
      this.pageHistory.push({
        path: this.currentPage,
        duration_ms: now - this.currentPageEntry,
        referrer: document.referrer || undefined,
      })
    }
    this.currentPage = path
    this.currentPageEntry = now
    // 清理超出 10 分钟窗口的历史记录
    const cutoff = now - this.PAGE_HISTORY_WINDOW_MS
    // 保留最近一条在窗口之前的记录作为上下文，其余丢弃
    let keptOneBefore = false
    this.pageHistory = this.pageHistory.filter(p => {
      const pageEndTime = this.currentPageEntry // 近似：上一页结束 ≈ 当前页开始
      if (pageEndTime >= cutoff) return true
      if (!keptOneBefore) { keptOneBefore = true; return true }
      return false
    })
    // 安全上限：最多保留 50 条
    if (this.pageHistory.length > 50) {
      this.pageHistory = this.pageHistory.slice(-50)
    }
    // 触发 SPA 路由切换上报（由 KsenseClient 注入）
    // 首次构造时 silent=true，避免调用方尚未完成初始化就触发上报
    if (!silent && this.onPageVisit) {
      this.onPageVisit()
    }
  }

  /** Monkey-patch history.pushState / replaceState，捕获 SPA 路由切换 */
  private monkeyPatchHistory(): void {
    const tracker = this
    const origPushState = history.pushState.bind(history)
    const origReplaceState = history.replaceState.bind(history)

    history.pushState = function (state, title, url) {
      const result = origPushState(state, title, url)
      tracker.trackPageVisit(tracker.getCurrentPath())
      return result
    }
    history.replaceState = function (state, title, url) {
      const result = origReplaceState(state, title, url)
      tracker.trackPageVisit(tracker.getCurrentPath())
      return result
    }
  }

  private bindEvents() {
    // 监听表单焦点：input/select/textarea 获得焦点时开始计时
    document.addEventListener('focusin', (e) => {
      const target = e.target as HTMLElement
      if (target.matches('input, select, textarea')) {
        if (this.formFocusTime === null) {
          this.formFocusTime = Date.now()
        }
      }
    }, { passive: true })
    // 监听表单焦点：失去焦点时累计填表时间
    document.addEventListener('focusout', (e) => {
      const target = e.target as HTMLElement
      if (target.matches('input, select, textarea')) {
        if (this.formFocusTime !== null) {
          this.totalFormFillMs += Date.now() - this.formFocusTime
          this.formFocusTime = null
        }
      }
    }, { passive: true })
    // 监听浏览器前进/后退
    window.addEventListener('popstate', () => { this.trackPageVisit(this.getCurrentPath()) })
    // 监听 hash 路由变化
    window.addEventListener('hashchange', () => { this.trackPageVisit(this.getCurrentPath()) })
    // 鼠标移动、键盘、粘贴事件
    document.addEventListener('mousemove', () => { this.mouseMoved = true }, { passive: true })
    document.addEventListener('keydown', () => { this.keystrokes++ })
    document.addEventListener('paste', () => { this.pasteCount++ })
  }

  getBehaviorData(): BehaviorData {
    // 如果当前焦点在表单中，加上正在填写的这一段
    let total = this.totalFormFillMs
    if (this.formFocusTime !== null) {
      total += Date.now() - this.formFocusTime
    }
    // 关闭当前页的计时，纳入历史
    const now = Date.now()
    const history = [...this.pageHistory]
    if (this.currentPage && this.currentPageEntry > 0) {
      history.push({
        path: this.currentPage,
        duration_ms: now - this.currentPageEntry,
        referrer: document.referrer || undefined,
      })
    }
    return {
      form_fill_duration_ms: total,
      page_session_duration_ms: now - this.sessionStart,
      mouse_movements: this.mouseMoved,
      keystrokes: this.keystrokes,
      paste_count: this.pasteCount,
      page_visible: !document.hidden,
      touch_supported: 'ontouchstart' in window,
      page_history: history,
    }
  }
}

// ============================================================
// 加密压缩工具
// 使用 RSA-OAEP + AES-GCM 混合加密，防止数据被拦截查看和篡改
// ============================================================

/** 使用 CompressionStream 进行 gzip 压缩（Chrome 80+, FF 113+, Safari 16.4+） */
async function compressGzip(data: Uint8Array): Promise<Uint8Array> {
  if (typeof CompressionStream === 'undefined') {
    return data // 不支持时跳过压缩
  }
  const cs = new CompressionStream('gzip')
  const writer = cs.writable.getWriter()
  writer.write(data as any)
  writer.close()
  const reader = cs.readable.getReader()
  const chunks: Uint8Array[] = []
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
  }
  const totalLen = chunks.reduce((sum, c) => sum + c.length, 0)
  const result = new Uint8Array(totalLen)
  let offset = 0
  for (const c of chunks) {
    result.set(c, offset)
    offset += c.length
  }
  return result
}

/** 导入 RSA 公钥（PEM 格式，SPKI 格式） */
async function importPublicKey(pem: string): Promise<CryptoKey> {
  const pemHeader = '-----BEGIN PUBLIC KEY-----'
  const pemFooter = '-----END PUBLIC KEY-----'
  const b64 = pem.replace(pemHeader, '').replace(pemFooter, '').replace(/\s/g, '')
  const der = Uint8Array.from(atob(b64), c => c.charCodeAt(0))
  return crypto.subtle.importKey(
    'spki', der,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false, ['encrypt'],
  )
}

/**
 * 混合加密 payload
 * 1. JSON 序列化 → 2. gzip 压缩 → 3. AES-256-GCM 加密
 * 4. RSA 加密 AES 密钥 → 5. 打包为 base64
 */
async function encryptPayload(data: unknown, publicKey: CryptoKey): Promise<string> {
  // 1. 序列化
  const jsonStr = JSON.stringify(data)
  const encoded = new TextEncoder().encode(jsonStr)

  // 2. 压缩
  const compressed = await compressGzip(encoded)

  // 3. 生成 AES-256-GCM 密钥
  const aesKey = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, ['encrypt'],
  )

  // 4. AES-GCM 加密数据
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encrypted = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, compressed as any),
  )

  // 5. RSA 加密 AES 密钥
  const rawAesKey = new Uint8Array(await crypto.subtle.exportKey('raw', aesKey))
  const encryptedKey = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, rawAesKey),
  )

  // 6. 打包: [IV(12)][keyLen(2)][encryptedKey][encryptedData]
  const keyLen = new Uint8Array([encryptedKey.length >> 8, encryptedKey.length & 0xFF])
  const packed = new Uint8Array(12 + 2 + encryptedKey.length + encrypted.length)
  packed.set(iv, 0)
  packed.set(keyLen, 12)
  packed.set(encryptedKey, 14)
  packed.set(encrypted, 14 + encryptedKey.length)

  // 7. Base64 编码
  const binary = Array.from(packed).map(b => String.fromCharCode(b)).join('')
  return btoa(binary)
}

// ============================================================
// HTTP POST 工具（支持明文和加密两种模式）
// ============================================================

async function postJSON(url: string, data: unknown, timeoutMs: number, publicKey?: CryptoKey): Promise<any> {
  let body: string
  if (publicKey) {
    // 加密模式
    body = JSON.stringify({ encrypted: await encryptPayload(data, publicKey) })
  } else {
    // 明文模式（降级）
    body = JSON.stringify(data)
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: controller.signal,
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    return await response.json()
  } finally {
    clearTimeout(timeoutId)
  }
}

// ============================================================
// 节流工具
// ============================================================

/** 创建节流函数：在 intervalMs 内最多执行一次，尾调用会被保留 */
function throttle<T extends (...args: any[]) => void>(fn: T, intervalMs: number): T {
  let lastRun = 0
  let timer: ReturnType<typeof setTimeout> | null = null
  return ((...args: any[]) => {
    const now = Date.now()
    const remaining = intervalMs - (now - lastRun)
    if (remaining <= 0) {
      if (timer) { clearTimeout(timer); timer = null }
      lastRun = now
      fn(...args)
    } else if (!timer) {
      timer = setTimeout(() => {
        timer = null
        lastRun = Date.now()
        fn(...args)
      }, remaining)
    }
  }) as T
}

// ============================================================
// 风控 SDK 客户端
// ============================================================

export class KsenseClient {
  private config: Required<SDKConfig>
  private fingerprint: DeviceFingerprint | null = null
  private rcToken: string | null = null
  private publicKey: CryptoKey | null = null
  private behaviorTracker: BehaviorTracker
  private initialized = false
  private destroyed = false

  // AJAX/fetch 拦截相关
  private origFetch: typeof window.fetch | null = null
  private origXHRSend: typeof XMLHttpRequest.prototype.send | null = null
  private ajaxInterceptInstalled = false

  // beforeunload 加密载荷缓存（避免页面卸载时异步加密不可用）
  private lastEncryptedBehaviorBody: string | null = null

  // 当前登录用户 ID（由商户调用 setUser 设置）
  private userId: string | null = null

  constructor(config: SDKConfig) {
    if (!config.merchantId) {
      console.warn('[Ksense] merchantId is required but not provided')
    }
    this.config = {
      merchantId: config.merchantId,
      endpoint: config.endpoint.replace(/\/$/, ''),
      cookieDomain: config.cookieDomain || '',
      cookiePath: config.cookiePath || '/',
      cookieMaxAge: config.cookieMaxAge ?? 2592000,
      enableDeviceFingerprint: config.enableDeviceFingerprint ?? true,
      enableBehaviorTracking: config.enableBehaviorTracking ?? true,
      debug: config.debug ?? false,
      timeout: config.timeout ?? 3000,
    }

    // 从 localStorage 恢复已有的 rc_token（跨 session 持久化）
    this.rcToken = loadRcToken()
    if (this.rcToken) {
      this.log('Existing rc_token restored from localStorage')
    }

    // 创建 BehaviorTracker，注入 SPA 路由切换上报回调（节流 2s）
    const throttledRouteReport = throttle(() => {
      this.reportBehavior('route_change')
    }, 2000)
    this.behaviorTracker = new BehaviorTracker(throttledRouteReport)
    this.log('KsenseClient initialized')
  }

  /**
   * 初始化设备指纹和行为数据采集并自动上报。
   *
   * 静默完成以下工作：
   * 1. 获取 RSA 公钥（用于加密）
   * 2. 从 localStorage 读取已有 rc_token（如有则带到上报请求中）
   * 3. 采集设备指纹（Canvas/WebGL/Audio/Fonts 等）
   * 4. 采集当前行为数据
   * 5. 加密压缩后上报到风控系统
   * 6. 服务端判断设备是否匹配：
   *    - 匹配 → 复用旧 token，刷新 TTL 为 30 天
   *    - 不匹配 → 颁发新 token
   * 7. 将 token 写入 localStorage（持久化）和 Cookie（表单自动提交）
   * 8. 注册 submit / visibilitychange / beforeunload 事件
   *
   * 商户前端只需在页面加载时调用一次 init()。
   * 应在页面加载时尽早调用，通过 requestIdleCallback 不阻塞首屏。
   */
  async init(): Promise<void> {
    if (this.initialized || this.destroyed) return
    this.initialized = true

    if (!this.config.enableDeviceFingerprint) {
      this.log('Device fingerprint disabled, skipping init')
      return
    }

    // 延迟到浏览器空闲时采集，不影响首屏渲染
    await new Promise<void>(resolve => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => resolve(), { timeout: 500 })
      } else {
        setTimeout(resolve, 200)
      }
    })

    // 获取 RSA 公钥（加密用），失败则降级为明文
    try {
      const pubKeyResp = await fetch(`${this.config.endpoint}/api/v1/session/key`, { signal: AbortSignal.timeout(5000) })
      if (pubKeyResp.ok) {
        const pem = await pubKeyResp.text()
        this.publicKey = await importPublicKey(pem)
        this.log('RSA public key loaded')
      }
    } catch {
      this.log('Failed to fetch public key, sending in plaintext')
    }

    // 采集设备指纹
    this.fingerprint = await FingerprintCollector.collect()
    this.log('Device fingerprint collected')

    // 采集当前行为数据
    const behavior = this.config.enableBehaviorTracking
      ? this.behaviorTracker.getBehaviorData()
      : undefined

    // 上报设备指纹 + 行为数据到风控服务端
    try {
      const sessionId = this.getSessionId()
      const payload: Record<string, any> = {
        merchant_id: this.config.merchantId,
        sdk_version: SDK_VERSION,
        session_id: sessionId,
        timestamp: new Date().toISOString(),
        ...this.fingerprint,
      }
      // 携带已有的 rc_token（如有），服务端判断是否可复用
      if (this.rcToken) {
        payload.rc_token = this.rcToken
      }
      if (behavior) {
        payload.behavior = behavior
      }
      if (this.userId) {
        payload.user_id = this.userId
      }

      const result = await postJSON(
        `${this.config.endpoint}/api/v1/session/report`,
        payload,
        this.config.timeout,
        this.publicKey ?? undefined,
      )

      // 从响应中提取 rc_token
      if (result?.code === 0 && result?.data?.rc_token) {
        this.rcToken = result.data.rc_token as string

        // 写入 localStorage（跨 session 持久化）
        saveRcToken(this.rcToken)

        // 写入 Cookie（表单提交自动携带给商户后端）
        setCookie(COOKIE_TOKEN_KEY, this.rcToken, {
          domain: this.config.cookieDomain || undefined,
          path: this.config.cookiePath,
          maxAge: this.config.cookieMaxAge,
          sameSite: 'Lax',
        })
        this.log('rc_token saved to localStorage and cookie')

        // 注册事件驱动的行为数据上报
        this.registerLifecycleEvents()
      }
      if (result?.code === 0 && result?.data?.fp) {
        this.log('Fingerprint reported, fp:', result.data.fp)
      }
    } catch (err) {
      this.log('Fingerprint report failed (non-blocking):', err)
    }
  }

  /**
   * 销毁 SDK 实例，清理事件监听。
   * 在单页应用（SPA）路由切换时应调用此方法。
   */
  destroy(): void {
    this.destroyed = true
    window.removeEventListener('visibilitychange', this.visibilityChangeHandler)
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    document.removeEventListener('submit', this.formSubmitHandler, true)
    // 卸载 AJAX/fetch 拦截
    this.uninstallAjaxIntercept()
  }

  /**
   * 手动刷新行为数据到服务端。
   * 适用于 AJAX 表单提交（非传统 form 提交）的场景：
   * ```ts
   * // 在发送 AJAX 请求前调用
   * await riskClient.flushBehavior()
   * // 然后发 AJAX 请求，后端从 Cookie 取 __rc_token
   * ```
   */
  async flushBehavior(): Promise<void> {
    await this.reportBehavior('manual_flush')
  }

  /**
   * 设置当前登录用户 ID。
   * 商户在用户登录后调用此方法，后续行为上报将自动关联用户。
   * 用户登出时调用 setUser(null) 清除。
   */
  setUser(userId: string | null): void {
    this.userId = userId
    this.log(userId ? `User set: ${userId}` : 'User cleared')
  }

  // ==================== 内部方法 ====================

  /** 注册事件驱动的行为数据上报 */
  private registerLifecycleEvents(): void {
    if (!this.config.enableBehaviorTracking) return
    window.addEventListener('visibilitychange', this.visibilityChangeHandler)
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
    // 捕获阶段监听所有表单提交，确保在导航前上报行为数据
    document.addEventListener('submit', this.formSubmitHandler, true)
    // 拦截 AJAX/fetch 请求，自动在请求前上报行为数据
    this.installAjaxIntercept()
  }

  /** form submit: 表单提交前上报行为数据（优先于 beforeunload），加密发送 */
  private formSubmitHandler = (): void => {
    if (this.destroyed || !this.rcToken) return
    this.reportBehaviorFireAndForget('form_submit')
  }

  // ==================== AJAX/fetch 拦截 ====================

  /** 节流版 AJAX 行为上报（3s 内最多一次，避免每次请求都上报） */
  private throttledAjaxReport = throttle(() => {
    this.reportBehaviorFireAndForget('ajax_request')
  }, 3000)

  /**
   * 火即忘方式上报行为数据（加密 + keepalive fetch，不阻塞调用方）。
   * 用于 form_submit / ajax_request 等不能 await 的场景。
   */
  private reportBehaviorFireAndForget(trigger: string): void {
    if (!this.rcToken || !this.config.enableBehaviorTracking || !this.behaviorTracker) return
    const behavior = this.behaviorTracker.getBehaviorData()
    // 异步加密并发送，不 await——keepalive 确保浏览器在页面卸载时也不丢请求
    this.sendEncryptedBehavior(trigger, behavior).catch(() => {})
  }

  /** 加密并发送行为数据（keepalive），同时更新 beforeunload 缓存 */
  private async sendEncryptedBehavior(trigger: string, behavior: BehaviorData): Promise<void> {
    const data: Record<string, any> = {
      rc_token: this.rcToken,
      timestamp: new Date().toISOString(),
      trigger,
      behavior,
    }
    if (this.userId) {
      data.user_id = this.userId
    }
    let body: string
    if (this.publicKey) {
      body = JSON.stringify({ encrypted: await encryptPayload(data, this.publicKey) })
    } else {
      body = JSON.stringify(data)
    }
    // 缓存最新加密载荷供 beforeunload 使用
    this.lastEncryptedBehaviorBody = body
    await fetch(`${this.config.endpoint}/api/v1/session/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    })
  }

  /** 判断 URL 是否为 SDK 自身上报端点（避免拦截自己的请求造成死循环） */
  private isSdkEndpoint(url: string): boolean {
    return url.includes('/api/v1/session/report') ||
      url.includes('/api/v1/session/update') ||
      url.includes('/api/v1/session/key')
  }

  /** 安装 fetch 拦截 */
  private installFetchIntercept(): void {
    if (this.ajaxInterceptInstalled) return
    const self = this
    this.origFetch = window.fetch

    window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      // 解析 URL
      let url: string
      if (typeof input === 'string') {
        url = input
      } else if (input instanceof Request) {
        url = input.url
      } else {
        url = input.toString()
      }
      // 跳过 SDK 自身的上报请求
      if (!self.isSdkEndpoint(url)) {
        self.throttledAjaxReport()
      }
      return self.origFetch!.call(window, input, init)
    }
  }

  /** 安装 XMLHttpRequest 拦截 */
  private installXHRIntercept(): void {
    if (this.ajaxInterceptInstalled) return
    const self = this
    this.origXHRSend = XMLHttpRequest.prototype.send

    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      // 通过闭包捕获 open 时设置的 URL
      const xhr = this as XMLHttpRequest & { __rc_url?: string }
      const url = xhr.__rc_url
      if (url && !self.isSdkEndpoint(url)) {
        self.throttledAjaxReport()
      }
      return self.origXHRSend!.call(this, body)
    }

    // 同时拦截 open 以记录 URL
    const origOpen = XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.open = function (
      method: string, url: string | URL, async?: boolean,
      user?: string | null, password?: string | null,
    ) {
      (this as any).__rc_url = typeof url === 'string' ? url : url.toString()
      return origOpen.call(this, method, url, async ?? true, user ?? null, password ?? null)
    }
  }

  /** 安装 AJAX/fetch 拦截（在 rc_token 就绪后调用） */
  private installAjaxIntercept(): void {
    if (this.ajaxInterceptInstalled) return
    this.ajaxInterceptInstalled = true
    try { this.installFetchIntercept() } catch { /* 静默 */ }
    try { this.installXHRIntercept() } catch { /* 静默 */ }
  }

  /** 卸载 AJAX/fetch 拦截 */
  private uninstallAjaxIntercept(): void {
    if (!this.ajaxInterceptInstalled) return
    this.ajaxInterceptInstalled = false
    if (this.origFetch) {
      window.fetch = this.origFetch
      this.origFetch = null
    }
    if (this.origXHRSend) {
      XMLHttpRequest.prototype.send = this.origXHRSend
      this.origXHRSend = null
    }
  }

  /** visibilitychange: 用户从其他标签页切回来时上报行为数据 */
  private visibilityChangeHandler = (): void => {
    if (this.destroyed || !this.rcToken) return
    if (document.visibilityState === 'visible') {
      this.reportBehavior('tab_switch')
    }
  }

  /** beforeunload: 页面关闭/刷新前上报最终行为数据（优先使用预加密缓存，降级明文） */
  private beforeUnloadHandler = (): void => {
    if (this.destroyed || !this.rcToken) return
    let body: string
    if (this.lastEncryptedBehaviorBody) {
      // 使用最近一次加密上报时缓存的载荷（已加密）
      body = this.lastEncryptedBehaviorBody
    } else {
      // 降级：明文 sendBeacon（首次访问、公钥未就绪等场景）
      const behavior = this.behaviorTracker.getBehaviorData()
      body = JSON.stringify({
        rc_token: this.rcToken,
        timestamp: new Date().toISOString(),
        trigger: 'beforeunload',
        behavior,
      })
    }
    navigator.sendBeacon(
      `${this.config.endpoint}/api/v1/session/update`,
      body,
    )
  }

  /** 上报行为数据到服务端（异步等待，用于 route_change / tab_switch / manual_flush） */
  private async reportBehavior(trigger: string): Promise<void> {
    if (!this.rcToken || !this.config.enableBehaviorTracking || !this.behaviorTracker) return
    const behavior = this.behaviorTracker.getBehaviorData()
    try {
      await this.sendEncryptedBehavior(trigger, behavior)
      this.log(`Behavior reported (trigger: ${trigger})`)
    } catch {
      // 上报失败静默处理
    }
  }

  /** 获取/生成浏览器会话 ID（session cookie，跨标签页共享，浏览器关闭后自动清除） */
  private getSessionId(): string {
    const cookieKey = '__rc_sid'
    let sid = getCookie(cookieKey)
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      setCookie(cookieKey, sid, {
        path: this.config.cookiePath || '/',
        domain: this.config.cookieDomain || undefined,
        sameSite: 'Lax',
        // 不设 maxAge → session cookie，浏览器关闭后自动清除
      })
    }
    return sid
  }

  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log('[Ksense]', ...args)
    }
  }
}

// ============================================================
// 异步加载 & 自动初始化
//
// 支持三种使用模式：
//
// 1. 同步加载 + 手动初始化（原有方式）
//    <script src="k-sense.1.0.0.js"></script>
//    <script>new Ksense.KsenseClient({...}).init()</script>
//
// 2. data-* 属性自动初始化（async/defer 均支持）
//    <script data-merchant-id="xxx" data-endpoint="https://..." async
//            src="k-sense.1.0.0.js"></script>
//
// 3. 命令队列异步加载（仿 Google Analytics gtag 模式）
//    <script>
//      window.Ksense = window.Ksense || []
//      window.Ksense.push(['init', { merchantId: 'xxx', endpoint: '...' }])
//    </script>
//    <script async src="k-sense.1.0.0.js"></script>
//
// 支持的命令：init / flush / destroy / setUser
//   setUser 用于关联登录用户：
//     window.Ksense.push(['setUser', '10001'])   // 登录
//     window.Ksense.push(['setUser', null])       // 登出
// ============================================================

declare global {
  interface Window {
    __ksClient: KsenseClient | undefined
    Ksense: Array<[string, ...any[]]> | undefined
  }
}

/** 从 script 标签的 data-* 属性读取配置（兼容 sync / async / defer） */
function parseScriptConfig(): SDKConfig | null {
  // 同步加载时 document.currentScript 可用
  let script = document.currentScript as HTMLScriptElement | null

  // async/defer 加载时 currentScript 为 null，遍历查找匹配的 script 标签
  if (!script) {
    const candidates = document.querySelectorAll('script[data-merchant-id]')
    for (const s of candidates) {
      const src = s.getAttribute('src') || ''
      if (src.includes('k-sense')) {
        script = s as HTMLScriptElement
        break
      }
    }
  }
  if (!script) return null

  const merchantId = script.getAttribute('data-merchant-id')
  const endpoint = script.getAttribute('data-endpoint')
  if (!merchantId || !endpoint) return null

  const config: SDKConfig = { merchantId, endpoint }

  const cookieDomain = script.getAttribute('data-cookie-domain')
  if (cookieDomain) config.cookieDomain = cookieDomain

  const debug = script.getAttribute('data-debug')
  if (debug === 'true') config.debug = true

  const timeout = script.getAttribute('data-timeout')
  if (timeout) config.timeout = parseInt(timeout, 10)

  const disableFP = script.getAttribute('data-disable-fingerprint')
  if (disableFP === 'true') config.enableDeviceFingerprint = false

  const disableBehavior = script.getAttribute('data-disable-behavior')
  if (disableBehavior === 'true') config.enableBehaviorTracking = false

  return config
}

/** 处理全局命令队列（异步加载前 push 的命令会在脚本加载后依次执行） */
function processQueue(): void {
  const q = window.Ksense
  if (!q || !Array.isArray(q)) return

  const commands = q.splice(0, q.length)

  for (const cmd of commands) {
    if (!Array.isArray(cmd) || cmd.length === 0) continue
    const [action, ...args] = cmd

    switch (action) {
      case 'init': {
        const cfg = args[0] as SDKConfig
        if (cfg && cfg.merchantId && cfg.endpoint) {
          const client = new KsenseClient(cfg)
          client.init()
          window.__ksClient = client
        }
        break
      }
      case 'flush': {
        window.__ksClient?.flushBehavior()
        break
      }
      case 'destroy': {
        window.__ksClient?.destroy()
        window.__ksClient = undefined
        break
      }
      case 'setUser': {
        const uid = args[0] as string | null
        window.__ksClient?.setUser(uid)
        break
      }
    }
  }
}

/** 包装全局队列，让脚本加载后再 push 的命令也能实时处理 */
function setupQueue(): void {
  const q = window.Ksense || []
  if (Array.isArray(q) && !('__processed' in q)) {
    const origPush = q.push.bind(q)
    q.push = function (...items: any[]) {
      const result = origPush(...items)
      processQueue()
      return result
    }
    ;(q as any).__processed = true
    window.Ksense = q
  }
}

/** 自动检测并初始化 */
function autoInit(): void {
  setupQueue()

  // 优先处理命令队列（命令队列方式优先级高于 data-*）
  processQueue()

  // 然后检查 data-* 配置
  const config = parseScriptConfig()
  if (config) {
    const client = new KsenseClient(config)
    client.init()
    window.__ksClient = client

    // 读取 data-user-id 属性（服务端渲染时注入当前登录用户 ID）
    const userId = getScriptDataAttr('data-user-id')
    if (userId) {
      client.setUser(userId)
    }
  }
}

/** 从 k-sense 的 script 标签读取 data-* 属性值 */
function getScriptDataAttr(attr: string): string | null {
  let script = document.currentScript as HTMLScriptElement | null
  if (!script) {
    const candidates = document.querySelectorAll('script[data-merchant-id]')
    for (const s of candidates) {
      if ((s.getAttribute('src') || '').includes('k-sense')) {
        script = s as HTMLScriptElement
        break
      }
    }
  }
  return script?.getAttribute(attr) || null
}

// DOM 就绪后执行（async 脚本可能在 DOMContentLoaded 前后到达）
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit)
} else {
  autoInit()
}
